import { useEffect, useRef, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, CircularProgress, IconButton,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { doc, setDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../config/firebase";

const modernDisplayFont = "Satoshi, Inter, SF Pro Display, system-ui, sans-serif";

const inputSx = {
  width: "100%",
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid rgba(255,255,255,0.2)",
  background: "rgba(255,255,255,0.06)",
  color: "#fff",
  fontSize: 13,
  boxSizing: "border-box",
  outline: "none",
  resize: "vertical",
};

// Shared edit dialog used by AdminRewards and the Points.js reward cards.
// Handles name/description/cost text editing and image upload to Firebase Storage.
export default function RewardEditDialog({ reward, onClose, onSaved }) {
  const [form, setForm]             = useState({ name: "", description: "", cost: "" });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [savedMsg, setSavedMsg]     = useState(false);
  const fileInputRef = useRef(null);

  // Sync local form whenever the reward prop changes (new card opened)
  useEffect(() => {
    if (reward) {
      setForm({
        name:        reward.name        || "",
        description: reward.description || "",
        cost:        String(reward.cost ?? ""),
      });
      setPreviewUrl(reward.imageUrl || null);
      setSavedMsg(false);
    }
  }, [reward]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !reward) return;

    console.log("Uploading image for reward:", reward);
    console.log("Reward ID:", reward?.id);
    console.log("Reward document path:", `redeemableRewards/${reward?.id}`);

    setPreviewUrl(URL.createObjectURL(file));
    setUploadingImg(true);
    try {
      const sRef = ref(storage, `reward-images/${reward.id}-${Date.now()}`);
      await uploadBytes(sRef, file);
      const downloadUrl = await getDownloadURL(sRef);

      await setDoc(doc(db, "redeemableRewards", reward.id), { imageUrl: downloadUrl }, { merge: true });

      setPreviewUrl(downloadUrl);
      onSaved({ id: reward.id, imageUrl: downloadUrl });
    } catch (err) {
      console.error("Reward image upload failed:", err);
      console.error("Firebase error code:", err.code);
      console.error("Firebase error message:", err.message);
    } finally {
      setUploadingImg(false);
    }
  };

  const handleSave = async () => {
    const costNum = parseInt(form.cost, 10);
    if (!Number.isFinite(costNum) || costNum < 1) {
      alert("Points Required must be a positive number.");
      return;
    }

    console.log("Saving reward:", reward);
    console.log("Reward ID:", reward?.id);
    console.log("Reward document path:", `redeemableRewards/${reward?.id}`);

    setSaving(true);
    try {
      const updates = {
        name:        form.name.trim(),
        description: form.description.trim(),
        cost:        costNum,
      };
      await setDoc(doc(db, "redeemableRewards", reward.id), updates, { merge: true });
      onSaved({ id: reward.id, ...updates });
      setSavedMsg(true);
      setTimeout(onClose, 1200);
    } catch (err) {
      console.error("Reward save failed:", err);
      console.error("Firebase error code:", err.code);
      console.error("Firebase error message:", err.message);
      alert("Save failed. Check your admin permissions.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={Boolean(reward)}
      onClose={() => { if (!saving && !uploadingImg) onClose(); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#0d1b4c",
          border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: "16px",
        },
      }}
    >
      {reward && (
        <>
          <DialogTitle
            sx={{
              fontFamily: modernDisplayFont,
              fontWeight: 600,
              color: "white",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <EditIcon sx={{ fontSize: 18 }} /> Edit — {reward.name}
            <IconButton
              onClick={onClose}
              disabled={saving || uploadingImg}
              sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>

            {/* Image upload section */}
            <Box>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  fontSize: 12,
                  letterSpacing: 1,
                  textTransform: "uppercase",
                  mb: 1,
                }}
              >
                Reward Photo
              </Typography>
              <Box sx={{ position: "relative", borderRadius: "10px", overflow: "hidden", mb: 1 }}>
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="preview"
                    style={{ width: "100%", height: 160, objectFit: "cover", display: "block", borderRadius: 10 }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%", height: 160, borderRadius: "10px",
                      background: "rgba(255,255,255,0.06)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: "rgba(255,255,255,0.35)", fontSize: 14,
                    }}
                  >
                    No image set
                  </Box>
                )}
                {uploadingImg && (
                  <Box
                    sx={{
                      position: "absolute", inset: 0,
                      backgroundColor: "rgba(0,0,0,0.55)",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      borderRadius: "10px",
                    }}
                  >
                    <CircularProgress size={32} sx={{ color: "white" }} />
                  </Box>
                )}
              </Box>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleImageChange}
                style={{ display: "none" }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={<PhotoCameraIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImg}
                sx={{
                  color: "rgba(255,255,255,0.7)",
                  borderColor: "rgba(255,255,255,0.25)",
                  borderRadius: "8px",
                  fontSize: 12,
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.6)",
                    backgroundColor: "rgba(255,255,255,0.05)",
                  },
                }}
              >
                {uploadingImg ? "Uploading…" : "Change Photo"}
              </Button>
            </Box>

            {/* Name */}
            <Box>
              <Typography
                sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12, letterSpacing: 1, textTransform: "uppercase", mb: 0.75 }}
              >
                Name
              </Typography>
              <input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                style={inputSx}
              />
            </Box>

            {/* Description */}
            <Box>
              <Typography
                sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12, letterSpacing: 1, textTransform: "uppercase", mb: 0.75 }}
              >
                Description
              </Typography>
              <textarea
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                rows={3}
                style={inputSx}
              />
            </Box>

            {/* Points Required */}
            <Box>
              <Typography
                sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12, letterSpacing: 1, textTransform: "uppercase", mb: 0.75 }}
              >
                Points Required
              </Typography>
              <input
                type="number"
                min={1}
                value={form.cost}
                onChange={(e) => setForm((p) => ({ ...p, cost: e.target.value }))}
                style={inputSx}
              />
            </Box>

          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2, justifyContent: "flex-end" }}>
            <Button
              onClick={onClose}
              disabled={saving || uploadingImg}
              sx={{
                color: "rgba(255,255,255,0.6)",
                border: "1px solid rgba(255,255,255,0.2)",
                borderRadius: "8px",
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || uploadingImg}
              sx={{
                backgroundColor: savedMsg ? "#1a7a4a" : "white",
                color: savedMsg ? "white" : "#0d1b4c",
                fontWeight: 600,
                borderRadius: "8px",
                "&:hover": { backgroundColor: savedMsg ? "#1a7a4a" : "#f0f0f0" },
              }}
            >
              {saving ? "Saving…" : savedMsg ? "✓ Saved!" : "Save Changes"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
